import React from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import { AiNodeQuota } from "src/aiNode/types";
import { formatLargeNumbers } from "src/aiNode/utils";
import { Icon } from "src/base-components/Icon";
import { Tooltip } from "src/design-system/Tooltip";

type AiNodeUsageTooltipProps = {
  aiNodeQuotas: AiNodeQuota | undefined;
  aiNodeTestRunRowLimit: number;
  aiNodeModelId: string;
};

type UsageLimitDisplayProps = {
  usageTitle: string;
  usageValue: number;
  usageLimit: number;
  usagePrefix?: string;
  usageSuffix?: string;
  limitPrefix?: string;
  formatLargeNumber: boolean;
};

const UsageLimitDisplay: React.FC<UsageLimitDisplayProps> = ({
  usageTitle,
  usageValue,
  usageLimit,
  usagePrefix = "",
  usageSuffix = "",
  limitPrefix = "",
  formatLargeNumber,
}) => {
  const formattedLimit = formatLargeNumber
    ? formatLargeNumbers(usageLimit)
    : usageLimit;
  return (
    <li>
      {usageTitle}:{" "}
      <b className="text-gray-100">
        {usagePrefix}
        {formatLargeNumber ? formatLargeNumbers(usageValue) : usageValue} /{" "}
      </b>
      {usageLimit >= 0 ? `${limitPrefix}${formattedLimit}` : "Unlimited"}{" "}
      {usageSuffix}
    </li>
  );
};

type RateLimitDisplayProps = {
  requests_per_minute_limit: number;
  tokens_per_minute_limit: number;
  formatLargeNumber: boolean;
};

const RateLimitsDisplay: React.FC<RateLimitDisplayProps> = ({
  requests_per_minute_limit,
  tokens_per_minute_limit,
  formatLargeNumber,
}) => {
  return (
    <>
      <li>
        Requests per minute limit:{" "}
        <b className="text-gray-100">
          {formatLargeNumber
            ? formatLargeNumbers(requests_per_minute_limit)
            : requests_per_minute_limit}
        </b>
      </li>
      <li>
        Tokens per minute limit:{" "}
        <b className="text-gray-100">
          {formatLargeNumber
            ? formatLargeNumbers(tokens_per_minute_limit)
            : tokens_per_minute_limit}
        </b>
      </li>
    </>
  );
};

const DetailedUsageDisplay: React.FC<{
  aiNodeQuotas: AiNodeQuota | undefined;
  aiNodeModelId: string;
}> = ({ aiNodeQuotas, aiNodeModelId }) => {
  return (
    <>
      {aiNodeQuotas?.usage?.[aiNodeModelId] && (
        <UsageLimitDisplay
          formatLargeNumber={true}
          usageLimit={aiNodeQuotas?.usage?.[aiNodeModelId].token_quota}
          usageTitle="Monthly tokens"
          usageValue={aiNodeQuotas?.usage?.[aiNodeModelId].tokens_used || 0}
        />
      )}
      {aiNodeQuotas?.cost && (
        <UsageLimitDisplay
          formatLargeNumber={true}
          limitPrefix="$"
          usageLimit={aiNodeQuotas.cost.limit}
          usagePrefix="$"
          usageTitle="Monthly cost"
          usageValue={Math.round((aiNodeQuotas.cost.used || 0) * 10) / 10}
        />
      )}
      {aiNodeQuotas?.usage?.[aiNodeModelId] && (
        <RateLimitsDisplay
          formatLargeNumber={true}
          requests_per_minute_limit={
            aiNodeQuotas?.usage?.[aiNodeModelId].requests_per_minute_limit
          }
          tokens_per_minute_limit={
            aiNodeQuotas?.usage?.[aiNodeModelId].tokens_per_minute_limit
          }
        />
      )}
    </>
  );
};

export const AiNodeUsageTooltip: React.FC<AiNodeUsageTooltipProps> = ({
  aiNodeQuotas,
  aiNodeTestRunRowLimit,
  aiNodeModelId,
}) => {
  return (
    <Tooltip
      body={
        <div className="flex flex-col gap-y-2 font-inter-normal-12px text-gray-300">
          <p>
            The following limits apply to AI Nodes across all test runs and
            environments in your workspace:
          </p>
          <ul className="ml-4 list-disc">
            <DetailedUsageDisplay
              aiNodeModelId={aiNodeModelId}
              aiNodeQuotas={aiNodeQuotas}
            />
            <li>
              Test runs:{" "}
              <b className="text-gray-100">
                Maximum of {aiNodeTestRunRowLimit} rows in dataset
              </b>
            </li>
          </ul>
          Please contact us to discuss increasing your limits.
        </div>
      }
      placement="bottom-end"
      title="AI Node usage limits"
    >
      <div className="flex items-center rounded-[4px] bg-gray-100 pr-2 pl-1">
        <Icon color="text-gray-400" icon={faCircleInfo} size="2xs" />
        <span className="font-[450] font-inter-normal-12px text-[12px] text-gray-600 leading-[20px] [font-optical-size:14px]">
          Usage limits
        </span>
      </div>
    </Tooltip>
  );
};
